import { useState, useEffect, useRef, useCallback } from 'react';
import { getListarVOPCliente } from '../../../api/dashboard';

const useModalGridDashboardVOPHook = ({ modalGridVOP, setAlerta, setModalGridVOP }) => {
  const [isPending, setIsPending] = useState(false);
  const [meses, setMeses] = useState([]);
  const [totais, setTotais] = useState({
    totalAcumulado: 0,
    mediaMensal: 0,
    maiorVOP: 0
  });
  const [anos, setAnos] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');

  const formatarMoeda = value =>
    value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });

  const formatarPorcentagem = value =>
    `${value > 0 ? '+' : ''}${value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}%`;

  // Cache: { [ano]: { meses, totais }, yearsList: [] }
  const cacheRef = useRef({});

  const fetchData = useCallback(
    async (codCliente, anoParam) => {
      setIsPending(true);
      try {
        const params = { codCliente };
        if (anoParam) params.ano = anoParam;

        const res = await getListarVOPCliente(params);
        if (!res.data.success) {
          setAlerta({
            mensagem: res.data.mensagem || 'Erro na API de VOP',
            tipo: 'error'
          });
          setModalGridVOP(false);
          return;
        }

        const { meses: mesesApi, totais: totaisApi, anos: anosApi } = res.data.data;

        const sortedMeses = Object.keys(mesesApi)
          .sort((a, b) => Number(a) - Number(b))
          .map(key => mesesApi[key]);

        setMeses(sortedMeses);
        setTotais(totaisApi);

        if (!anoParam) {
          cacheRef.current.yearsList = anosApi;
          setAnos(anosApi);
          if (anosApi.length > 0) {
            setSelectedYear(anosApi[0]);
          }
        }

        const cacheKey = anoParam || (cacheRef.current.yearsList && cacheRef.current.yearsList[0]);
        if (cacheKey) {
          cacheRef.current[cacheKey] = {
            meses: sortedMeses,
            totais: totaisApi
          };
        }
      } catch (error) {
        setAlerta({
          mensagem: 'Erro ao buscar dados de VOP. Favor contatar o suporte.',
          tipo: 'error'
        });
        setModalGridVOP(false);
      } finally {
        setIsPending(false);
      }
    },
    [setAlerta]
  );

  useEffect(() => {
    const codCliente = modalGridVOP.params.id;
    if (!codCliente) return;

    if (cacheRef.current.yearsList) {
      setAnos(cacheRef.current.yearsList);
      const defaultYear = cacheRef.current.yearsList[0];
      setSelectedYear(defaultYear);
      const cached = cacheRef.current[defaultYear];
      if (cached) {
        setMeses(cached.meses);
        setTotais(cached.totais);
        return;
      }
    }

    fetchData(codCliente);
  }, [modalGridVOP, fetchData]);

  const handleChangeAno = useCallback(
    ano => {
      setSelectedYear(ano);
      const codCliente = modalGridVOP.params.id;
      if (!codCliente) return;

      if (cacheRef.current[ano]) {
        setMeses(cacheRef.current[ano].meses);
        setTotais(cacheRef.current[ano].totais);
      } else {
        fetchData(codCliente, ano);
      }
    },
    [modalGridVOP, fetchData]
  );

  return {
    isPending,
    meses,
    totais,
    anos,
    selectedYear,
    handleChangeAno,
    formatarMoeda,
    formatarPorcentagem
  };
};

export default useModalGridDashboardVOPHook;
